/**
 * Filter state manager for the ProjectReport page.
 *
 * All filtering and breakdown recomputation is now server-side.
 * This hook manages UI filter state and converts it to API query params.
 */

import { useState, useMemo, useCallback } from 'react';
import type {
  ProjectReportResponse,
  ReportQueryParams,
} from '@/types/api';

// =============================================================================
// Types
// =============================================================================

export type ScheduleFilter =
  | 'all'
  | 'overdue'
  | 'due_this_week'
  | 'due_next_week'
  | 'due_this_month'
  | 'no_due_date'
  | 'custom';

export interface ReportFilters {
  domain: string[];
  area: string[];
  tester: string[];
  status: string[];
  schedule: ScheduleFilter;
  hasIssues: boolean;
  customDateRange: { from?: Date; to?: Date };
}

export interface ReportFilterOptions {
  domains: string[];
  areas: string[];
  testers: string[];
  testerEmails: Record<string, string>;
}

export interface UseReportFiltersReturn {
  filters: ReportFilters;
  toggleDomainFilter: (v: string) => void;
  toggleAreaFilter: (v: string) => void;
  toggleTesterFilter: (v: string) => void;
  toggleStatus: (v: string) => void;
  setSchedule: (v: ScheduleFilter) => void;
  setHasIssues: (v: boolean) => void;
  setCustomDateRange: (v: { from?: Date; to?: Date }) => void;
  filterOptions: ReportFilterOptions;
  isFiltered: boolean;
  activeFilterCount: number;
  resetFilters: () => void;
  /** API query params derived from current filter state */
  filterParams: ReportQueryParams;
}

// =============================================================================
// Hook
// =============================================================================

const DEFAULT_FILTERS: ReportFilters = {
  domain: [],
  area: [],
  tester: [],
  status: [],
  schedule: 'all',
  hasIssues: false,
  customDateRange: {},
};

/**
 * Manages filter UI state and derives API query params.
 *
 * @param report - The current report response (used to extract filter options for dropdowns)
 */
export function useReportFilters(
  report: ProjectReportResponse | undefined,
): UseReportFiltersReturn {
  const [filters, setFilters] = useState<ReportFilters>(DEFAULT_FILTERS);

  // Toggle helpers for multi-select filters
  const makeToggle = (key: 'domain' | 'area' | 'tester' | 'status') =>
    (v: string) =>
      setFilters((f) => ({
        ...f,
        [key]: f[key].includes(v) ? f[key].filter((s) => s !== v) : [...f[key], v],
      }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggleDomainFilter = useCallback(makeToggle('domain'), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggleAreaFilter = useCallback(makeToggle('area'), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggleTesterFilter = useCallback(makeToggle('tester'), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggleStatus = useCallback(makeToggle('status'), []);
  const setSchedule = useCallback(
    (v: ScheduleFilter) => setFilters((f) => ({ ...f, schedule: v })),
    [],
  );
  const setHasIssues = useCallback(
    (v: boolean) => setFilters((f) => ({ ...f, hasIssues: v })),
    [],
  );
  const setCustomDateRange = useCallback(
    (v: { from?: Date; to?: Date }) => setFilters((f) => ({ ...f, customDateRange: v })),
    [],
  );
  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  // Filter options — prefer server-provided filter_options (unfiltered),
  // fall back to deriving from response data (for legacy/unfiltered requests)
  const filterOptions = useMemo<ReportFilterOptions>(() => {
    const emailMap = report?.tester_emails ?? {};

    // Server provides unfiltered filter options when using pagination/filters
    if (report?.filter_options) {
      return {
        domains: report.filter_options.domains,
        areas: report.filter_options.areas,
        testers: report.filter_options.testers,
        testerEmails: emailMap,
      };
    }

    // Fallback: derive from response data (legacy path without pagination)
    if (!report?.scenarios) return { domains: [], areas: [], testers: [], testerEmails: {} };
    const domainSet = new Set<string>();
    const areaSet = new Set<string>();
    const testerSet = new Set<string>();
    for (const s of report.scenarios) {
      if (s.domains) {
        for (const d of s.domains) domainSet.add(d);
      } else if (s.domain) {
        domainSet.add(s.domain);
      }
      if (s.areas) {
        for (const a of s.areas) areaSet.add(a);
      } else if (s.area) {
        areaSet.add(s.area);
      }
      for (const step of s.steps) {
        if (step.test_person) testerSet.add(step.test_person);
      }
    }
    if (report.tester_breakdown) {
      for (const t of report.tester_breakdown) {
        if (t.test_person) testerSet.add(t.test_person);
      }
    }
    return {
      domains: [...domainSet].sort(),
      areas: [...areaSet].sort(),
      testers: [...testerSet].sort(),
      testerEmails: emailMap,
    };
  }, [report]);

  const isFiltered =
    filters.domain.length > 0 ||
    filters.area.length > 0 ||
    filters.tester.length > 0 ||
    filters.status.length > 0 ||
    filters.schedule !== 'all' ||
    filters.hasIssues;

  const activeFilterCount = [
    filters.domain.length > 0,
    filters.area.length > 0,
    filters.tester.length > 0,
    filters.status.length > 0,
    filters.schedule !== 'all',
    filters.hasIssues,
  ].filter(Boolean).length;

  // Convert filter state to API query params
  const filterParams = useMemo<ReportQueryParams>(() => {
    const params: ReportQueryParams = {};
    if (filters.domain.length > 0) params.domain = filters.domain;
    if (filters.area.length > 0) params.area = filters.area;
    if (filters.tester.length > 0) params.tester = filters.tester;
    if (filters.status.length > 0) params.status = filters.status;
    if (filters.hasIssues) params.has_issues = true;
    if (filters.schedule !== 'all' && filters.schedule !== 'custom') {
      params.schedule = filters.schedule;
    }
    if (filters.schedule === 'custom') {
      if (filters.customDateRange.from) {
        params.date_from = filters.customDateRange.from.toISOString().slice(0, 10);
      }
      if (filters.customDateRange.to) {
        params.date_to = filters.customDateRange.to.toISOString().slice(0, 10);
      }
    }
    return params;
  }, [filters]);

  return {
    filters,
    toggleDomainFilter,
    toggleAreaFilter,
    toggleTesterFilter,
    toggleStatus,
    setSchedule,
    setHasIssues,
    setCustomDateRange,
    filterOptions,
    isFiltered,
    activeFilterCount,
    resetFilters,
    filterParams,
  };
}
